import { useState } from 'react';
import type { Customer } from '../services/customersApi';
import { useSetSowingPaymentThreshold } from '../hooks/useCustomer';
import { useNotification } from '@/shared/hooks/useNotification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck } from 'lucide-react';

interface SowingThresholdDialogProps {
  customer: Customer;
}

export const SowingThresholdDialog = ({ customer }: SowingThresholdDialogProps) => {
  const [open, setOpen] = useState(false);
  const [useOverride, setUseOverride] = useState(customer.sowingPaymentThresholdOverride != null);
  const [threshold, setThreshold] = useState<string>(
    customer.sowingPaymentThresholdOverride != null
      ? String(customer.sowingPaymentThresholdOverride)
      : '0'
  );

  const mutation = useSetSowingPaymentThreshold(customer.id);
  const notify = useNotification();

  const handleSave = () => {
    const value = useOverride ? Number(threshold) : null;

    if (useOverride && (isNaN(value!) || value! < 0 || value! > 100)) {
      notify.error('Threshold must be between 0 and 100');
      return;
    }

    mutation.mutate(value, {
      onSuccess: () => {
        notify.success(
          value != null
            ? `Sowing threshold set to ${value}% for ${customer.customerName}`
            : `Sowing threshold reset to system default for ${customer.customerName}`
        );
        setOpen(false);
      },
      onError: () => notify.error('Failed to update sowing threshold'),
    });
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      // Reset form state when opening
      setUseOverride(customer.sowingPaymentThresholdOverride != null);
      setThreshold(
        customer.sowingPaymentThresholdOverride != null
          ? String(customer.sowingPaymentThresholdOverride)
          : '0'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground w-full justify-start">
          <ShieldCheck className="w-4 h-4" />
          Sowing Threshold
          {customer.sowingPaymentThresholdOverride != null && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {customer.sowingPaymentThresholdOverride}%
            </Badge>
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Sowing Payment Threshold</DialogTitle>
          <DialogDescription>
            Override the minimum deposit % required before sowing can begin for{' '}
            <strong>{customer.customerName}</strong>. The system default is 50%.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={!useOverride}
                onChange={() => setUseOverride(false)}
                className="accent-primary"
              />
              <span className="text-sm">Use system default (50%)</span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                checked={useOverride}
                onChange={() => setUseOverride(true)}
                className="accent-primary"
              />
              <span className="text-sm">Custom threshold</span>
            </label>
          </div>

          {useOverride && (
            <Field>
              <FieldLabel>Minimum deposit percentage (0–100)</FieldLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              {threshold === '0' && (
                <p className="text-xs text-amber-600 mt-1">
                  Setting to 0% allows sowing with no payment at all.
                </p>
              )}
            </Field>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
